import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import MobileNav from "../components/MobileNav";
import { SHELL_BG } from "../components/AppShell";
import UserProfileMenu from "../components/UserProfileMenu";
import { showAlert } from "@/components/AppDialog";
import { API_BASE_URL } from "@/utils/env";
import { getIrReport, IR_STAGES } from "@/data/irReports";
import type { IrBlock, IrReport, Tone } from "@/data/irReports";
import { useReportContent } from "@/data/reportContent";
import {
  FileText, ArrowRight, ArrowUpRight, Activity, Search, Target,
  Wrench, Lightbulb, Rocket, Lock, Loader2,
} from "lucide-react";

// ── Palette / helpers ──────────────────────────────────────────────────────
const BRAND = "#1976D2";
const NAVY = "#17263A";

function bandFor(score: number): { color: string; label: string } {
  if (score <= 33) return { color: "#C0392B", label: "Critical Risk" };
  if (score <= 41) return { color: "#E0592A", label: "Very High Risk" };
  if (score <= 48) return { color: "#E08A1E", label: "High Risk" };
  if (score <= 56) return { color: "#E6B400", label: "Medium Risk" };
  if (score <= 65) return { color: "#1976D2", label: "Semi-Ready" };
  if (score <= 74) return { color: "#0E8F6E", label: "Investor-Ready" };
  return { color: "#15824E", label: "Exceptional" };
}

const TONE_STYLE: Record<Tone, { bg: string; bar: string; text: string }> = {
  red:     { bg: "#FBEAE7", bar: "#C0392B", text: "#8f2a1e" },
  orange:  { bg: "#FCEDE6", bar: "#E0592A", text: "#9c3c1a" },
  yellow:  { bg: "#FBF1DF", bar: "#E08A1E", text: "#8a5a12" },
  blue:    { bg: "#EAF2FB", bar: "#1976D2", text: "#125aa6" },
  green:   { bg: "#E7F4EC", bar: "#15824E", text: "#0f5c36" },
  neutral: { bg: "#EEF2F7", bar: "#6E7888", text: "#42505F" },
};

const SECTION_ICONS = [Activity, Search, Target, Wrench, Lightbulb, Rocket];

// ── Block renderer (on-page report preview) ─────────────────────────────────
function Block({ block }: { block: IrBlock }) {
  if (block.kind === "paragraph")
    return <p className="text-[14.5px] text-slate-600 leading-relaxed mb-3">{block.text}</p>;

  if (block.kind === "bullets")
    return (
      <ul className="space-y-2 mb-3">
        {block.items.map((it, i) => (
          <li key={i} className="flex gap-2.5 text-[14px] text-slate-600 leading-relaxed">
            <span className="mt-[7px] w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: BRAND }} />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    );

  if (block.kind === "callout") {
    const s = TONE_STYLE[block.tone ?? "blue"];
    return (
      <div className="flex rounded-xl overflow-hidden mb-3" style={{ backgroundColor: s.bg }}>
        <span className="w-1 shrink-0" style={{ backgroundColor: s.bar }} />
        <p className="px-4 py-3 text-[14px] font-bold leading-snug" style={{ color: NAVY }}>{block.text}</p>
      </div>
    );
  }

  // steps
  return (
    <div className="space-y-4 mb-2">
      {block.items.map((step, i) => (
        <div key={i}>
          <div className="flex items-center gap-2.5">
            <span className="w-6 h-6 rounded-lg flex items-center justify-center text-[12px] font-extrabold text-white shrink-0" style={{ backgroundColor: BRAND }}>{i + 1}</span>
            <p className="font-bold text-[14.5px]" style={{ color: NAVY }}>{step.title}</p>
          </div>
          {step.sub && step.sub.length > 0 && (
            <ul className="mt-2 ml-8 space-y-1.5">
              {step.sub.map((s, j) => (
                <li key={j} className="flex gap-2.5 text-[13.5px] text-slate-500 leading-relaxed">
                  <span className="mt-[7px] w-1.5 h-1.5 rounded-full shrink-0 bg-slate-300" />
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </div>
  );
}

export default function IRScore() {
  const navigate = useNavigate();
  useReportContent(); // load admin report-text overrides so the report + PDF reflect edits
  const [loading, setLoading] = useState(true);
  const [irData, setIrData] = useState<any>(null);
  const [goalGroup, setGoalGroup] = useState<string | null>(null);
  const [userName, setUserName] = useState("Founder");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const userStr = localStorage.getItem("user");
        if (!userStr) { navigate("/login"); return; }
        const user = JSON.parse(userStr);
        setUserName((user.firstName || user.displayName || "Founder").split(" ")[0]);
        const tokensStr = localStorage.getItem("authTokens");
        const idToken = tokensStr ? JSON.parse(tokensStr).idToken : null;
        const resp = await fetch(`${API_BASE_URL}/auth/user/${user.uid}`, {
          headers: { ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}) },
        });
        if (resp.ok) {
          const json = await resp.json();
          if (json.success) {
            if (json.user?.irScore) setIrData(json.user.irScore);
            if (json.user?.goalAssessment?.group) setGoalGroup(json.user.goalAssessment.group);
          }
        }
      } catch (e) {
        console.error("Error fetching IR score:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const totalScore: number = Math.round(irData?.totalScore ?? 0);
  const hasScore = !!irData && totalScore > 0;
  const report: IrReport | undefined = hasScore ? getIrReport(goalGroup, totalScore) : undefined;
  const band = bandFor(totalScore);
  const currentIndex = report ? IR_STAGES.findIndex((s) => s.rCode === report.rCode) : -1;
  const pct = Math.max(0, Math.min(100, totalScore));

  const handleOpenReport = async () => {
    if (!report) { navigate("/readiness-journey"); return; }
    const win = window.open("", "_blank"); // sync open → not popup-blocked
    try {
      setBusy(true);
      const { openIrReportPdf } = await import("@/utils/irReportPdf");
      await openIrReportPdf(report, {
        userName,
        score: totalScore,
        dateLabel: new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }),
      }, win);
      // Best-effort download-confirmation email (non-blocking).
      try {
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        if (user?.email) {
          fetch(`${API_BASE_URL}/auth/send-report-downloaded`, {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email: user.email, firstName: userName, reportScore: totalScore }),
          }).catch(() => {});
        }
      } catch { /* ignore */ }
    } catch (e) {
      console.error("Failed to generate report:", e);
      if (win && !win.closed) win.close();
      showAlert("Sorry, we could not generate your report. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const R = 84, C = 2 * Math.PI * R;

  return (
    <div className="flex min-h-screen" style={{ background: SHELL_BG }}>
      <Sidebar />
      <main className="flex-1 md:ms-64 overflow-y-auto relative">
        <MobileNav />
        <div className="absolute top-6 end-8 z-10 hidden md:block"><UserProfileMenu /></div>

        <div className="mx-auto max-w-[1080px] px-5 md:px-8 py-8">
          {loading ? (
            <div className="flex items-center justify-center h-[70vh]">
              <div className="text-center">
                <Loader2 className="w-9 h-9 animate-spin mx-auto mb-3" style={{ color: BRAND }} />
                <p className="text-slate-500 font-medium">Loading your IR Score…</p>
              </div>
            </div>
          ) : !hasScore ? (
            /* ── Empty state ── */
            <div className="mt-10 max-w-xl mx-auto text-center bg-white rounded-3xl border border-slate-100 shadow-sm p-10">
              <div className="w-14 h-14 rounded-2xl mx-auto mb-5 flex items-center justify-center" style={{ background: "linear-gradient(135deg,#2E83E6,#17263A)" }}>
                <FileText className="w-7 h-7 text-white" />
              </div>
              <h1 className="text-2xl font-extrabold mb-2" style={{ color: NAVY }}>Your Investor Readiness Score</h1>
              <p className="text-slate-500 mb-6 leading-relaxed">Complete the readiness assessment to unlock your IR Score and a personalised Founder Diagnostic Report.</p>
              <button onClick={() => navigate("/readiness-journey")}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white font-bold text-sm shadow-sm transition hover:opacity-90"
                style={{ backgroundColor: BRAND }}>
                Take the assessment <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <>
              {/* ── Header ── */}
              <header className="mb-6">
                <span className="text-[11px] font-bold uppercase tracking-[0.14em]" style={{ color: BRAND }}>Investor Readiness</span>
                <h1 className="text-3xl font-extrabold mt-1" style={{ color: NAVY }}>Your Founder Diagnostic</h1>
                <p className="text-slate-500 mt-1">Your readiness score, archetype and a tailored 90-day action plan.</p>
              </header>

              {/* ── Hero: score + profile ── */}
              <div className="rounded-3xl overflow-hidden shadow-sm mb-6" style={{ background: "linear-gradient(135deg,#1E63B8 0%,#1976D2 45%,#17263A 100%)" }}>
                <div className="grid md:grid-cols-[300px_1fr] gap-6 p-7 md:p-9">
                  {/* Score ring */}
                  <div className="flex flex-col items-center justify-center">
                    <div className="relative" style={{ width: 210, height: 210 }}>
                      <svg className="w-full h-full -rotate-90" viewBox="0 0 200 200">
                        <circle cx="100" cy="100" r={R} fill="none" stroke="rgba(255,255,255,0.14)" strokeWidth="14" />
                        <circle cx="100" cy="100" r={R} fill="none" stroke="#fff" strokeWidth="14" strokeLinecap="round"
                          strokeDasharray={`${C * pct / 100} ${C}`} style={{ transition: "stroke-dasharray 1s ease" }} />
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className="text-[56px] font-black text-white leading-none">{totalScore}</span>
                        <span className="text-[11px] font-semibold text-white/60 mt-1">out of 100</span>
                      </div>
                    </div>
                    <span className="mt-3 px-4 py-1.5 rounded-full text-[12px] font-bold bg-white" style={{ color: band.color }}>{band.label}</span>
                  </div>

                  {/* Profile */}
                  <div className="flex flex-col justify-center text-white">
                    <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-white/60">{report?.pillar}</span>
                    <h2 className="text-[34px] font-black leading-tight mt-1">{report?.profile}</h2>
                    <p className="text-white/85 text-[15px] leading-relaxed mt-2 max-w-lg">{report?.tagline}</p>
                    <div className="flex flex-wrap items-center gap-3 mt-6">
                      <button onClick={handleOpenReport} disabled={busy}
                        className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-white font-bold text-sm shadow-sm transition hover:bg-white/90 disabled:opacity-70"
                        style={{ color: BRAND }}>
                        {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Preparing…</> : <><FileText className="w-4 h-4" /> View full report <ArrowUpRight className="w-4 h-4" /></>}
                      </button>
                      <span className="text-[12px] text-white/60">Opens in a new tab. View online or download</span>
                    </div>
                  </div>
                </div>

                {/* Journey stepper */}
                <div className="px-7 md:px-9 pb-7 pt-1">
                  <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-white/50 mb-3">Your readiness journey</div>
                  <div className="flex items-center">
                    {IR_STAGES.map((st, i) => {
                      const done = i <= currentIndex;
                      const isCur = i === currentIndex;
                      return (
                        <div key={st.rCode} className="flex items-center flex-1 last:flex-none">
                          <div className="flex flex-col items-center">
                            <div className="rounded-full flex items-center justify-center transition-all"
                              style={{
                                width: isCur ? 22 : 14, height: isCur ? 22 : 14,
                                background: done ? "#fff" : "rgba(255,255,255,0.28)",
                                boxShadow: isCur ? "0 0 0 5px rgba(255,255,255,0.22)" : "none",
                              }}>
                              {isCur && <span className="w-2 h-2 rounded-full" style={{ backgroundColor: band.color }} />}
                            </div>
                            <span className={`mt-1.5 text-[9.5px] font-bold ${isCur ? "text-white" : "text-white/55"}`}>{st.rCode}</span>
                          </div>
                          {i < IR_STAGES.length - 1 && (
                            <div className="flex-1 h-[3px] mx-1 rounded-full -mt-4" style={{ background: i < currentIndex ? "#fff" : "rgba(255,255,255,0.22)" }} />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* ── Report sections (online view) ── */}
              <div className="grid md:grid-cols-2 gap-5">
                {report?.sections.map((section, i) => {
                  const Icon = SECTION_ICONS[i] ?? FileText;
                  const isCTA = section.title === "Call to Action";
                  return (
                    <div key={i}
                      className={`bg-white rounded-2xl border border-slate-100 shadow-sm p-6 ${(i === 0 || isCTA) ? "md:col-span-2" : ""}`}>
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                          style={{ background: isCTA ? "linear-gradient(135deg,#2E83E6,#17263A)" : "#EAF2FB" }}>
                          <Icon className="w-[18px] h-[18px]" style={{ color: isCTA ? "#fff" : BRAND }} />
                        </div>
                        <h3 className="text-[16px] font-extrabold" style={{ color: NAVY }}>{section.title}</h3>
                      </div>
                      {section.blocks.map((b, j) => <Block key={j} block={b} />)}
                    </div>
                  );
                })}
              </div>

              {/* Footer CTA */}
              <div className="mt-6 rounded-2xl border border-slate-100 bg-white shadow-sm p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "#EAF2FB" }}>
                    <Lock className="w-5 h-5" style={{ color: BRAND }} />
                  </div>
                  <div>
                    <p className="font-bold text-[15px]" style={{ color: NAVY }}>Your full diagnostic report is ready</p>
                    <p className="text-[13px] text-slate-500">Beautifully formatted PDF, opens in a new tab to view or download.</p>
                  </div>
                </div>
                <button onClick={handleOpenReport} disabled={busy}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white font-bold text-sm shadow-sm transition hover:opacity-90 disabled:opacity-70 shrink-0"
                  style={{ backgroundColor: BRAND }}>
                  {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Preparing…</> : <><FileText className="w-4 h-4" /> View full report</>}
                </button>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
